import { useCallback, useEffect, useRef, useState } from 'react';

import { transcribeAudio } from '../../../services/ai-explorer/aiExplorerService';

const BLUETOOTH_WORDS = ['airpod', 'bluetooth', 'wireless', 'bt ', 'headset', 'headphone', 'earphone', 'buds'];

const pickMime = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
  if (MediaRecorder.isTypeSupported('audio/webm')) return 'audio/webm';
  return '';
};

/**
 * Record from a microphone, send the clip to the transcription endpoint and
 * hand the text back. Mirrors what the legacy page did, minus the native
 * alerts: failures surface through `onError` so the page can show them its way.
 *
 * `onTranscript(text)` receives the recognised text; `onError(key, detail)`
 * an i18n key (`agent.micDenied`, `agent.transcribeFailed`).
 */
export default function useVoiceInput({ onTranscript, onError }) {
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [mics, setMics] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedMicId, setSelectedMicId] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedRef = useRef(null);
  const tickRef = useRef(null);

  const releaseStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') recorderRef.current.stop();
    releaseStream();
    if (tickRef.current) clearInterval(tickRef.current);
  }, []);

  const startWithDevice = useCallback(async (deviceId) => {
    const audio = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
    if (deviceId) audio.deviceId = { exact: deviceId };
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio });
    } catch {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        onError?.('agent.micDenied');
        return;
      }
    }

    const mime = pickMime();
    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
    recorderRef.current = recorder;
    streamRef.current = stream;
    chunksRef.current = [];
    startedRef.current = Date.now();
    setElapsed(0);
    tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedRef.current) / 1000)), 500);

    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = async () => {
      const duration = Date.now() - (startedRef.current || Date.now());
      const chunks = chunksRef.current;
      chunksRef.current = [];
      recorderRef.current = null;
      startedRef.current = null;
      if (tickRef.current) clearInterval(tickRef.current);
      releaseStream();
      setListening(false);

      const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
      if (chunks.length === 0 || duration < 500 || blob.size < 100) return;

      setTranscribing(true);
      try {
        const res = await transcribeAudio(blob);
        const text = res.data?.text?.trim();
        // The provider answers a silent clip with an apology or a bracketed note.
        if (text && !text.startsWith('[') && !/^I('m| am) sorry|^I can'?t/i.test(text)) onTranscript?.(text);
      } catch {
        onError?.('agent.transcribeFailed');
      } finally {
        setTranscribing(false);
      }
    };

    recorder.start(250);
    setListening(true);
  }, [onError, onTranscript]);

  /** Ask for permission, list inputs and pick a Bluetooth headset when there is one. */
  const resolveMic = useCallback(async () => {
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      probe.getTracks().forEach((t) => t.stop());
    } catch {
      onError?.('agent.micDenied');
      return undefined;
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    const inputs = devices.filter((d) => d.kind === 'audioinput' && d.deviceId !== 'default' && d.deviceId !== 'communications');
    setMics(inputs);

    if (selectedMicId && inputs.some((m) => m.deviceId === selectedMicId)) return selectedMicId;
    const bt = inputs.find((m) => BLUETOOTH_WORDS.some((w) => m.label.toLowerCase().includes(w)));
    if (bt) return bt.deviceId;
    if (inputs.length > 1) { setPickerOpen(true); return null; }
    return inputs.length === 1 ? inputs[0].deviceId : undefined;
  }, [onError, selectedMicId]);

  const toggle = useCallback(async () => {
    if (listening) {
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
      return;
    }
    if (pickerOpen) { setPickerOpen(false); return; }
    if (!navigator.mediaDevices?.getUserMedia) { onError?.('agent.micDenied'); return; }
    const deviceId = await resolveMic();
    if (deviceId === null) return;              // the picker is open; wait for a choice
    await startWithDevice(deviceId);
  }, [listening, onError, pickerOpen, resolveMic, startWithDevice]);

  const cancel = useCallback(() => {
    chunksRef.current = [];
    startedRef.current = Date.now();              // makes the clip look too short to send
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const chooseMic = useCallback((deviceId) => {
    setSelectedMicId(deviceId);
    setPickerOpen(false);
    startWithDevice(deviceId);
  }, [startWithDevice]);

  return {
    listening, transcribing, elapsed, mics, pickerOpen,
    toggle, cancel, chooseMic, closePicker: () => setPickerOpen(false),
  };
}
